import Select from "react-select";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-hot-toast";
import { BASE_URL_STORAGE } from "../../../helpers/config";
import { readImageFile } from "../../../utils/helpers/imageUpload";
import { useMemo, useRef } from "react";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";

const FormCustomer = (props) => {
  const {
    isShowModal: _isShowModal,
    isEdit,
    closeModal,
    formState,
    setFormState,
    handleAdd,
    handleUpdate,
    cities,
    modalId = "modal-form",
    modalClassName = "",
    isLoading = false,
  } = props;
  const formRef = useRef(null);
  const validationSchema = useMemo(() => ({
    name: rule.required("Nama pelanggan wajib diisi."),
    phone: [
      rule.requiredWhen((values) => [true, 1, "1", "true"].includes(values?.is_member), "Nomor HP member wajib diisi."),
      rule.phone(),
    ],
    category: [rule.required("Kategori pelanggan wajib dipilih."), rule.oneOf(["personal", "business", "other"])],
  }), []);
  const validation = useFormValidation({ values: formState, schema: validationSchema, formId: "customer", focusRoot: formRef });

  const submit = async (handler) => {
    if (!validation.validateAll() || isLoading) return;
    await handler();
  };

  const optionCities = (cities || []).map((city) => {
    return {
      value: city?.id,
      label: city?.name,
    };
  });
  const optionCategory = [
    { value: "personal", label: "Perorangan" },
    { value: "business", label: "Bisnis" },
    { value: "other", label: "Lainnya" },
  ];
  const isMember = [true, 1, "1", "true"].includes(formState?.is_member);

  return (
    <div
      className={`modal modal-blur fade ${modalClassName}`.trim()}
      id={modalId}
      tabIndex="-1"
      role="dialog"
      aria-hidden="false"
      data-bs-backdrop="static"
      data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div ref={formRef} className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Master Pelanggan {!isEdit ? "Baru" : "Edit"}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={closeModal}></button>
          </div>
          <div className="modal-body">
            <FormValidationNotice errors={validation.errors} />
            <div className="row">
              <div className="col-lg-12">
                <label className="form-label">Nama Lengkap<RequiredMark /></label>
                <div className="mb-3 input-icon">
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-1">
                      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
                      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                    </svg>
                  </span>
                  <input
                    {...validation.fieldProps("name")}
                    type="text"
                    className={validation.inputClass("name")}
                    placeholder="nama lengkap"
                    value={formState?.name || ""}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <FieldFeedback formId="customer" name="name" error={validation.errors.name} hint="Nama ini akan tampil pada transaksi dan riwayat pelanggan." />
                </div>
              </div>
              <div className="col-lg-12 mb-3">
                <label className="form-label mb-2">Status Pelanggan</label>
                <div className="akas-binary-choice" role="radiogroup" aria-label="Status member pelanggan">
                  <label className={!isMember ? "is-active" : ""}>
                    <input
                      type="radio"
                      name="customer-membership"
                      checked={!isMember}
                      onChange={() =>
                        setFormState((previous) => ({ ...previous, is_member: 0, is_display_landing: 0 }))
                      }
                    />
                    <span>
                      <strong>Bukan member</strong>
                      <small>Cukup simpan nama pelanggan</small>
                    </span>
                  </label>
                  <label className={isMember ? "is-active" : ""}>
                    <input
                      type="radio"
                      name="customer-membership"
                      checked={isMember}
                      onChange={() => setFormState((previous) => ({ ...previous, is_member: 1 }))}
                    />
                    <span>
                      <strong>Member</strong>
                      <small>Lengkapi profil dan kontak</small>
                    </span>
                  </label>
                </div>
              </div>
              {isMember ? (
                <>
                  <div className="col-lg-12">
                    <label className="form-label">No HP<RequiredMark /></label>
                    <div className="mb-3 input-icon">
                      <span className="input-icon-addon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-brand-whatsapp">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9" />
                          <path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1" />
                        </svg>
                      </span>
                      <input
                        {...validation.fieldProps("phone")}
                        type="text"
                        className={validation.inputClass("phone")}
                        placeholder="no hp"
                        value={formState?.phone || ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                          }))
                        }
                      />
                      <FieldFeedback formId="customer" name="phone" error={validation.errors.phone} hint="Wajib untuk member; gunakan 8–15 digit agar identitas member dapat dicari." />
                    </div>
                  </div>

                  <div className="col-lg-12" style={{ display: optionCities.length > 0 ? undefined : "none" }}>
                    <label className="form-label">Asal Kabupaten / Kota<RequiredMark optional /></label>
                    <div className="mb-3 input-icon">
                      <span
                        className="input-icon-addon"
                        style={{
                          zIndex: 1,
                        }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-map-2">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 18.5l-3 -1.5l-6 3v-13l6 -3l6 3l6 -3v7.5" />
                          <path d="M9 4v13" />
                          <path d="M15 7v5.5" />
                          <path d="M21.121 20.121a3 3 0 1 0 -4.242 0c.418 .419 1.125 1.045 2.121 1.879c1.051 -.89 1.759 -1.516 2.121 -1.879" />
                          <path d="M19 18v.01" />
                        </svg>
                      </span>
                      <Select
                        options={optionCities || []}
                        name="city_id"
                        className="form-select p-0"
                        classNamePrefix="akas-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "2rem", // beri ruang untuk ikon
                            border: "none",
                            borderRadius: "8px",
                            boxShadow: "none",
                            minHeight: "38px",
                            "&:hover": {
                              border: "none",
                              boxShadow: "none",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "#e3f2fd", // soft light blue
                            borderRadius: "20px",
                            padding: "2px 8px",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "#a7f3d0", // soft blue text
                            fontWeight: "500",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "#a7f3d0",
                            ":hover": {
                              backgroundColor: "#bbdefb", // hover soft blue
                              color: "#a7f3d0",
                            },
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "#32CD32" // item yang dipilih
                              : state.isFocused
                                ? "#90EE90" // saat hover
                                : "#FFF", // default
                            color: state.isSelected
                              ? "#FFF" // item yang dipilih
                              : state.isFocused
                                ? "#000" // saat hover
                                : "#000", // default
                            cursor: "pointer",
                          }),
                          // list dropdown style
                          menu: (base) => ({
                            ...base,
                            zIndex: 2,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: "#90a4ae", // muted blue-gray
                          }),
                        }}
                        value={
                          formState?.city_id ? optionCities.find((option) => option.value === formState?.city_id) : null
                        }
                        onChange={(selectedOptions) => {
                          setFormState({
                            ...formState,
                            city_id: selectedOptions?.value || "",
                            city_name: selectedOptions?.label || "",
                          });
                        }}
                        placeholder="-- Pilih Kota --"
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <label className="form-label">Kategori<RequiredMark /></label>
                    <div className="mb-3 input-icon">
                      <span
                        className="input-icon-addon"
                        style={{
                          zIndex: 9999,
                        }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-category">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M4 4h6v6h-6l0 -6" />
                          <path d="M14 4h6v6h-6l0 -6" />
                          <path d="M4 14h6v6h-6l0 -6" />
                          <path d="M14 17a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                        </svg>
                      </span>
                      <Select
                        {...validation.fieldProps("category")}
                        data-validation-field="category"
                        options={optionCategory || []}
                        name="category"
                        className={validation.inputClass("category", "form-select p-0")}
                        classNamePrefix="akas-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "2rem", // beri ruang untuk ikon
                            border: "none",
                            borderRadius: "8px",
                            boxShadow: "none",
                            minHeight: "38px",
                            "&:hover": {
                              border: "none",
                              boxShadow: "none",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "#e3f2fd", // soft light blue
                            borderRadius: "20px",
                            padding: "2px 8px",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "#a7f3d0", // soft blue text
                            fontWeight: "500",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "#a7f3d0",
                            ":hover": {
                              backgroundColor: "#bbdefb", // hover soft blue
                              color: "#a7f3d0",
                            },
                          }),
                          // list dropdown style
                          menu: (base) => ({
                            ...base,
                            zIndex: 2,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: "#90a4ae", // muted blue-gray
                          }),
                        }}
                        value={
                          formState?.category
                            ? optionCategory.find((option) => option.value === formState?.category)
                            : null
                        }
                        onChange={(selectedOptions) => {
                          setFormState({
                            ...formState,
                            category: selectedOptions?.value || "",
                            category_name: selectedOptions?.label || "",
                          });
                        }}
                        placeholder="-- Pilih Kategori --"
                      />
                      <FieldFeedback formId="customer" name="category" error={validation.errors.category} hint="Pilih Perorangan, Bisnis, atau Lainnya." />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <label className="form-label">Foto / Logo Mitra</label>
                    <div className="mb-3 w-100 ">
                      <div className="card d-flex justify-content-center align-content-center w-100">
                        <DragDropFile setFormState={setFormState} formState={formState} />
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-12 mt-3">
                    <label className="form-label mb-2">Tampilkan di Landing Page</label>

                    <div className="card bg-light border-0 rounded-3">
                      <div className="card-body p-2">
                        <div className="d-flex position-relative bg-white rounded-2 p-1">
                          <div
                            className={`position-absolute top-0 bottom-0 rounded-2 transition-all ${
                              formState.is_display_landing ? "bg-primary" : "bg-secondary"
                            }`}
                            style={{
                              width: "50%",
                              transition: "transform 0.3s ease",
                              transform: formState.is_display_landing ? "translateX(100%)" : "translateX(0)",
                              zIndex: 0,
                            }}></div>

                          <div
                            className={`flex-fill text-center py-2 rounded-2 position-relative z-1 transition-all ${
                              !formState.is_display_landing ? "text-white" : "text-dark"
                            }`}
                            onClick={() => setFormState({ ...formState, is_display_landing: 0 })}
                            style={{ cursor: "pointer", transition: "all 0.3s ease" }}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-eye-off me-2">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M3 3l18 18" />
                              <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                              <path d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4 0 7.333 2.333 10 7c-.778 1.361 -1.612 2.447 -2.503 3.258" />
                              <path d="M6.357 6.349c-1.335 .92 -2.785 2.455 -4.357 5.651c2.667 4.667 6 7 10 7c1.371 0 2.633 -.267 3.786 -.802" />
                            </svg>
                            Sembunyikan
                          </div>

                          <div
                            className={`flex-fill text-center py-2 rounded-2 position-relative z-1 transition-all ${
                              formState.is_display_landing ? "text-white" : "text-dark"
                            }`}
                            onClick={() => setFormState({ ...formState, is_display_landing: 1 })}
                            style={{ cursor: "pointer", transition: "all 0.3s ease" }}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-eye me-2">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                              <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                            </svg>
                            Tampilkan
                          </div>
                        </div>
                        <div className="form-hint px-2 pt-2">
                          Aktifkan jika pelanggan/mitra ini ingin ditampilkan pada section “Our Mitra” di landing page.
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
              {/* <div className="col-lg-12">
                  <label className="form-label  mb-2">Status Reseller</label>
                  <div className="card bg-light border-0 rounded-3">
                    <div className="card-body p-2">
                      <div className="d-flex position-relative bg-white rounded-2 p-1">
                        <div
                          className={`position-absolute top-0 bottom-0 rounded-2 transition-all ${formState.is_reseller ? "bg-primary" : "bg-danger"}`}
                          style={{
                            width: "50%",
                            transition: "transform 0.3s ease",
                            transform: formState.is_reseller ? "translateX(100%)" : "translateX(0)",
                            zIndex: 0,
                          }}></div>
                         <div
                          className={`flex-fill text-center py-2 rounded-2 position-relative z-1 transition-all ${
                            !formState.is_reseller ? "text-white" : "text-dark"
                          }`}
                          onClick={() => setFormState({ ...formState, is_reseller: 0 })}
                          style={{ cursor: "pointer", transition: "all 0.3s ease" }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-user-shield me-2">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M6 21v-2a4 4 0 0 1 4 -4h2" />
                            <path d="M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5" />
                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                          </svg>
                          Bukan Reseller
                        </div>
                        <div
                          className={`flex-fill text-center py-2 rounded-2 position-relative z-1 transition-all ${
                            formState.is_reseller ? "text-white" : "text-dark"
                          }`}
                          onClick={() => setFormState({ ...formState, is_reseller: 1 })}
                          style={{ cursor: "pointer", transition: "all 0.3s ease" }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-user-shield me-2">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M6 21v-2a4 4 0 0 1 4 -4h2" />
                            <path d="M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5" />
                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                          </svg>
                          Reseller
                        </div>
                      </div>
                    </div>
                  </div>
                 </div> */}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-link link-secondary btn-3"
              data-bs-dismiss="modal"
              onClick={closeModal}>
              Batal
            </button>
            {isEdit ? (
              <button
                type="button"
                className="btn btn-primary btn-5 ms-auto"
                disabled={isLoading}
                onClick={() => submit(handleUpdate)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                  <path d="M16 5l3 3" />
                </svg>
                {isLoading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-5 ms-auto"
                disabled={isLoading}
                onClick={() => submit(handleAdd)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-2">
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
                {isLoading ? "Menyimpan..." : "Tambah Pelanggan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const resolvePreviewImage = (formState) => {
  const imageValue = formState?.imageUrl || formState?.image_path || formState?.image;

  if (!imageValue) {
    return "";
  }

  if (
    String(imageValue).startsWith("data:") ||
    String(imageValue).startsWith("blob:") ||
    String(imageValue).startsWith("http")
  ) {
    return imageValue;
  }

  return BASE_URL_STORAGE + imageValue;
};

const DragDropFile = (props) => {
  const { setFormState, formState } = props;
  const fileTypes = ["JPG", "JPEG", "PNG", "WEBP"];
  const previewImage = resolvePreviewImage(formState);

  return (
    <FileUploader
      multiple={false}
      handleChange={async (file) => {
        try {
          const base64 = await readImageFile(file, { label: "Foto pelanggan", preset: "avatar" });
          const currentImagePath =
            formState?.image_path && !String(formState.image_path).startsWith("data:") ? formState.image_path : "";

          setFormState((previous) => ({
            ...previous,
            image: base64,
            image_path: currentImagePath,
            imageUrl: base64,
            file_name: file?.name,
          }));
        } catch (error) {
          toast.error(error?.message || "Foto pelanggan tidak dapat dibaca.");
        }
      }}
      name="file"
      types={fileTypes}
      dropMessageStyle={{ margin: "0  0.5rem" }}
      style={{ position: "relative" }}>
      <div className="d-flex align-items-center justify-content-center w-100 ">
        <label
          htmlFor="dropzone-file"
          className="d-flex flex-column align-items-center justify-content-center w-100 rounded cursor-pointer bg-light"
          style={{ height: "14rem" }}>
          <div className="d-flex flex-column align-items-center justify-content-center pt-3 pb-3">
            <svg
              className="mb-3 text-secondary"
              width="32"
              height="32"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 16">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5A5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
              />
            </svg>
            <p className="mb-2 text-muted small">
              <strong>Click Untuk upload</strong> atau drag and drop
            </p>
            <p className="text-muted small">JPG, PNG, atau WEBP (maks. 5 MB, dioptimalkan otomatis)</p>
          </div>
        </label>
      </div>

      {previewImage ? (
        <div
          className="d-flex align-items-center justify-content-center w-100 position-absolute bottom-0 z-0 border border-primary rounded transition-opacity"
          style={{
            height: "14rem",
            opacity: "1",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "0.5")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}>
          <img src={previewImage} className="rounded h-100 w-100 p-2 object-fit-contain" alt="Uploaded preview" />
        </div>
      ) : (
        ""
      )}
    </FileUploader>
  );
};

export default FormCustomer;
